import { useNavigate } from "react-router-dom";
import { C, GlassCard, Badge } from "@coremonit/design-system";
import {
  Boxes, GitBranch, ContainerIcon, FolderKanban, Server, Terminal,
  Activity, AlertTriangle, GitCommit, ArrowUpRight,
} from "lucide-react";
import type { ResourceLinks } from "@coremonit/types";

const LINK_DEFS: Array<{ key: keyof ResourceLinks; label: string; icon: typeof Boxes; path: (l: ResourceLinks) => string | null }> = [
  { key: "serviceId", label: "Service", icon: Boxes, path: (l) => l.serviceId ? `/services/${l.serviceId}` : null },
  { key: "deploymentId", label: "Deployment", icon: GitBranch, path: (l) => l.deploymentId ? `/deployments` : null },
  { key: "podId", label: "Pod", icon: ContainerIcon, path: (l) => l.serviceId && l.podId ? `/containers/${l.serviceId}/${l.podId}` : null },
  { key: "containerName", label: "Container", icon: ContainerIcon, path: (l) => l.serviceId && l.podId && l.containerName ? `/containers/${l.serviceId}/${l.podId}/${encodeURIComponent(l.containerName)}` : null },
  { key: "clusterId", label: "Cluster", icon: Server, path: (l) => l.clusterId ? `/clusters/${l.clusterId}` : null },
  { key: "projectId", label: "Project", icon: FolderKanban, path: () => `/projects` },
  { key: "traceId", label: "Trace", icon: Activity, path: (l) => l.traceId ? `/traces/${l.traceId}` : null },
  { key: "incidentId", label: "Incident", icon: AlertTriangle, path: (l) => l.incidentId ? `/alerts` : null },
  { key: "gitCommit", label: "Git commit", icon: GitCommit, path: () => `/deployments` },
];

export function RelatedResources({ links, title = "Related resources" }: { links: ResourceLinks; title?: string }) {
  const nav = useNavigate();
  const available = LINK_DEFS.filter((d) => links[d.key]);

  if (!available.length) return null;

  return (
    <GlassCard style={{ padding: 14 }}>
      <div style={{ color: C.textSub, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 }}>{title}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {available.map((d) => {
          const Icon = d.icon;
          const path = d.path(links);
          const val = String(links[d.key]);
          return (
            <button
              key={d.key}
              onClick={() => path && nav(path)}
              disabled={!path}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 10px", borderRadius: 8,
                background: C.surfaceHigh, border: `1px solid ${C.border}`,
                color: C.text, fontSize: 10, fontWeight: 600, cursor: path ? "pointer" : "default",
              }}
            >
              <Icon size={11} color={C.blue} />
              {d.label}
              <Badge color="muted">{val.length > 16 ? `${val.slice(0, 14)}…` : val}</Badge>
              {path && <ArrowUpRight size={10} color={C.textMuted} />}
            </button>
          );
        })}
        <button onClick={() => nav("/logs")} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 8, background: C.surfaceHigh, border: `1px solid ${C.border}`, color: C.text, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>
          <Terminal size={11} color={C.cyan} /> Logs
        </button>
        <button onClick={() => nav("/metrics")} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 8, background: C.surfaceHigh, border: `1px solid ${C.border}`, color: C.text, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>
          <Activity size={11} color={C.green} /> Metrics
        </button>
      </div>
    </GlassCard>
  );
}
